/**
 * PermissionsTab — grouped view of the canonical permission
 * vocabulary. Sprint P37o · Feb 2026.
 */
import { useQuery } from "@tanstack/react-query";
import { Key } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { TabSkeleton, TabError } from "@/pages/superadmin/tabs/RolesTab";

const GROUP_LABEL = {
  users:          "Users",
  companies:      "Companies",
  projects:       "Projects",
  data:           "Data",
  gis:            "GIS",
  estimation:     "Estimation",
  classification: "Classification",
  reports:        "Reports",
  cp_approval:    "CP Approval",
  admin:          "Administration",
};

export default function PermissionsTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["rbac", "permissions"],
    queryFn: () => api.get("/rbac/permissions").then((r) => r.data),
    staleTime: 60_000,
  });
  if (isLoading) return <TabSkeleton lines={10} />;
  if (error) return <TabError error={error} />;
  const groups = data?.grouped || {};
  return (
    <div data-testid="rbac-permissions-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-black text-2xl tracking-tight">Permissions Catalogue</h2>
          <p className="mt-1 text-[12px] text-slate-500 max-w-3xl">
            The complete permission vocabulary, grouped by domain. Each permission is a
            fine-grained verb on a domain noun. Roles combine these into coherent job
            functions — see the <span className="text-brand-blue font-medium">Roles</span> tab.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          {data?.total || 0} permissions
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(groups).map(([group, perms]) => (
          <div key={group} className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                data-testid={`permission-group-${group}`}>
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
              <Key size={13} className="text-brand-blue" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-700 font-semibold">
                {GROUP_LABEL[group] || group}
              </div>
              <div className="ml-auto text-[11px] font-mono text-slate-500">{perms.length}</div>
            </div>
            <ul className="p-3 space-y-1.5">
              {perms.map((p) => (
                <li key={p} className="flex items-center gap-2 text-[12.5px]">
                  <span className="w-1 h-1 rounded-full bg-brand-blue" />
                  <span className="font-mono text-slate-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
